import {
  ChallengeCategoryCode,
  ChallengeCategoryGender,
  ChallengeDetail,
} from "../../challenges/types/challengeAdmin";
import { getPocoPairsCount } from "../../challenges/utils/pocoSchedule";
import { sortRegistrationCategories } from "../../challenges/utils/registrationCategoryOrder";

export type TournamentRegistrationCategoryItem = NonNullable<
  ChallengeDetail["registrationCategories"]
>[number];

export const DEFAULT_TOURNAMENT_SHIRT_SIZES = [
  "P",
  "M",
  "G",
  "GG",
  "Babylook P",
  "Babylook M",
  "Babylook G",
  "Babylook GG",
];

export function buildUniqueTournamentCategoryKey(
  categories: readonly { categoryKey?: string | null }[],
  timestamp = Date.now(),
) {
  const existingKeys = new Set(
    categories.map((category) => String(category.categoryKey || "").trim()),
  );
  const baseKey = `categoria-${timestamp}`;
  if (!existingKeys.has(baseKey)) return baseKey;

  let suffix = 2;
  while (existingKeys.has(`${baseKey}-${suffix}`)) {
    suffix += 1;
  }
  return `${baseKey}-${suffix}`;
}

export function normalizeCategoryCodeForTournament(
  value: unknown,
  fallback: ChallengeCategoryCode = "6a",
): ChallengeCategoryCode {
  const safe = String(value || "")
    .trim()
    .toLowerCase();
  if (
    safe === "7a" ||
    safe === "6a" ||
    safe === "5a" ||
    safe === "4a" ||
    safe === "3a" ||
    safe === "2a" ||
    safe === "open" ||
    safe === "free"
  ) {
    return safe;
  }
  return fallback;
}

export function normalizeTournamentCategoryGender(
  value: unknown,
  fallback: ChallengeCategoryGender = "mixed",
): ChallengeCategoryGender {
  const safe = String(value || "")
    .trim()
    .toLowerCase();
  if (safe === "male" || safe === "female" || safe === "mixed" || safe === "open")
    return safe;
  return fallback;
}

export function normalizeTournamentCategoryRegistrationType(
  value: unknown,
  fallback: "individual" | "fixed_pair" = "individual",
) {
  const safe = String(value || "")
    .trim()
    .toLowerCase();
  if (safe === "fixed_pair") return "fixed_pair";
  if (safe === "individual") return "individual";
  return fallback;
}

export function normalizeTournamentShirtSizes(value: unknown) {
  const input = Array.isArray(value)
    ? value
    : String(value || "")
        .split(/[,\n;]/g);
  const unique = new Set<string>();
  const sizes: string[] = [];
  input.forEach((item) => {
    const size = String(item || "").trim().slice(0, 24);
    if (!size) return;
    const key = size.toLowerCase();
    if (unique.has(key)) return;
    unique.add(key);
    sizes.push(size);
  });
  return sizes.slice(0, 20);
}

export function normalizeTournamentShirtSizesEnabled(
  value: unknown,
  shirtSizes: unknown,
) {
  if (typeof value === "boolean") return value;
  const safe = String(value ?? "")
    .trim()
    .toLowerCase();
  if (safe === "true" || safe === "1" || safe === "yes" || safe === "sim") {
    return true;
  }
  if (safe === "false" || safe === "0" || safe === "no" || safe === "nao") {
    return false;
  }
  return normalizeTournamentShirtSizes(shirtSizes).length > 0;
}

export function normalizeTournamentRegistrationCategoriesForEdit(
  categories: ChallengeDetail["registrationCategories"],
): TournamentRegistrationCategoryItem[] {
  const input = Array.isArray(categories) ? categories : [];
  const normalized = input
    .map((item, index) => {
      const categoryKey = String(item?.categoryKey || "").trim();
      const categoryName = String(item?.categoryName || "").trim();
      const slotLimit = Math.max(1, Math.floor(Number(item?.slotLimit || 0)));
      if (!Number.isFinite(slotLimit)) return null;
      const shirtSizes = normalizeTournamentShirtSizes(item?.shirtSizes);
      const shirtSizesEnabled = normalizeTournamentShirtSizesEnabled(
        item?.shirtSizesEnabled,
        shirtSizes,
      );
      return {
        categoryKey: categoryKey || `categoria-${index + 1}`,
        categoryName,
        ...([3, 4].includes(Number(item?.courtsCount)) ? { courtsCount: Number(item?.courtsCount) } : {}),
        categoryMale: normalizeCategoryCodeForTournament(item?.categoryMale),
        categoryFemale: normalizeCategoryCodeForTournament(item?.categoryFemale),
        categoryGender: normalizeTournamentCategoryGender(item?.categoryGender),
        registrationType: normalizeTournamentCategoryRegistrationType(
          item?.registrationType,
        ),
        address: String(item?.address || "").trim() || null,
        scheduledAt: String(item?.scheduledAt || "").trim() || null,
        scheduledEndAt: String(item?.scheduledEndAt || "").trim() || null,
        shirtSizesEnabled,
        shirtSizes: shirtSizesEnabled ? shirtSizes : [],
        slotLimit,
        activeRegistrations: Math.max(
          0,
          Math.floor(Number(item?.activeRegistrations || 0)),
        ),
        confirmedRegistrations: Math.max(
          0,
          Math.floor(Number(item?.confirmedRegistrations || 0)),
        ),
        remainingSlots: Math.max(
          0,
          Math.floor(Number(item?.remainingSlots || slotLimit)),
        ),
      } satisfies TournamentRegistrationCategoryItem;
    })
    .filter(Boolean) as TournamentRegistrationCategoryItem[];

  if (normalized.length > 0) return sortRegistrationCategories(normalized);

  return [
    {
      categoryKey: "categoria-1",
      categoryName: "Categoria 1",
      categoryMale: "6a",
      categoryFemale: "6a",
      categoryGender: "mixed",
      registrationType: "individual",
      address: null,
      scheduledAt: null,
      scheduledEndAt: null,
      shirtSizesEnabled: false,
      shirtSizes: [],
      slotLimit: 12,
      activeRegistrations: 0,
      confirmedRegistrations: 0,
      remainingSlots: 12,
    } satisfies TournamentRegistrationCategoryItem,
  ];
}

export function normalizeChallengeRegistrationCategoriesForEdit(
  detail: ChallengeDetail,
): TournamentRegistrationCategoryItem[] {
  const input = Array.isArray(detail.registrationCategories)
    ? detail.registrationCategories
    : [];
  const isPoco = detail.challengeType === "poco";
  const pocoPairLimit = getPocoPairsCount(detail.courtsCount);

  if (input.length > 0) {
    return normalizeTournamentRegistrationCategoriesForEdit(input).map(
      (category) =>
        isPoco
          ? {
              ...category,
              registrationType: "fixed_pair",
              courtsCount: Number(category.courtsCount) === 4 ? 4 : Number(category.courtsCount) === 3 ? 3 : Number(detail.courtsCount) === 4 ? 4 : 3,
              slotLimit: getPocoPairsCount(category.courtsCount ?? detail.courtsCount),
              remainingSlots: Math.max(
                0,
                getPocoPairsCount(category.courtsCount ?? detail.courtsCount) - Number(category.confirmedRegistrations || 0),
              ),
            }
          : category,
    );
  }

  const registrationType = isPoco
    ? "fixed_pair"
    : detail.registrationType === "fixed_pair"
      ? "fixed_pair"
      : "individual";
  const slotLimit = isPoco
    ? pocoPairLimit
    : Math.max(2, Math.floor(Number(detail.slotLimit || 12)));
  const participantsCount = Math.max(
    0,
    Math.floor(Number(detail.participantsCount || 0)),
  );
  const activeRegistrations =
    isPoco
      ? Math.ceil(participantsCount / 2)
      : participantsCount;
  const fallbackCategory = normalizeCategoryCodeForTournament(
    detail.category || detail.categoryMale || detail.categoryFemale,
  );
  const categoryGender = normalizeTournamentCategoryGender(
    detail.categoryGender,
  );
  const shirtSizes = normalizeTournamentShirtSizes(detail.shirtSizes);

  return [
    {
      categoryKey: "categoria-1",
      categoryName: "Categoria 1",
      categoryMale: normalizeCategoryCodeForTournament(
        detail.categoryMale,
        fallbackCategory,
      ),
      categoryFemale: normalizeCategoryCodeForTournament(
        detail.categoryFemale,
        fallbackCategory,
      ),
      categoryGender,
      registrationType,
      address: String(detail.address || "").trim() || null,
      scheduledAt: String(detail.scheduledAt || "").trim() || null,
      scheduledEndAt: null,
      shirtSizesEnabled: Boolean(detail.shirtSizesEnabled),
      shirtSizes: detail.shirtSizesEnabled ? shirtSizes : [],
      slotLimit,
      activeRegistrations,
      confirmedRegistrations: activeRegistrations,
      remainingSlots: Math.max(0, slotLimit - activeRegistrations),
    },
  ];
}

export function buildTournamentCategoriesPayload(
  categories: ChallengeDetail["registrationCategories"],
) {
  return normalizeTournamentRegistrationCategoriesForEdit(categories).map(
    (item) => ({
      categoryKey: String(item.categoryKey || "").trim() || undefined,
      categoryName: String(item.categoryName || "").trim(),
      courtsCount: item.courtsCount ?? undefined,
      categoryMale: normalizeCategoryCodeForTournament(item.categoryMale),
      categoryFemale: normalizeCategoryCodeForTournament(item.categoryFemale),
      categoryGender: normalizeTournamentCategoryGender(item.categoryGender),
      registrationType: normalizeTournamentCategoryRegistrationType(
        item.registrationType,
      ),
      address: String(item.address || "").trim() || null,
      scheduledAt: String(item.scheduledAt || "").trim() || null,
      scheduledEndAt: String(item.scheduledEndAt || "").trim() || null,
      shirtSizesEnabled: Boolean(item.shirtSizesEnabled),
      shirtSizes: item.shirtSizesEnabled
        ? normalizeTournamentShirtSizes(item.shirtSizes)
        : [],
      slotLimit: Math.max(1, Math.floor(Number(item.slotLimit || 0))),
    }),
  );
}
